import {Injectable} from '@angular/core';
import {HttpClient, HttpParams} from '@angular/common/http';
import {from, Observable, switchMap} from 'rxjs';
import {environment} from '../../environments/environment';
import {OrderView} from './order.service';

export interface DropshipMapping {
  mappingId?: string;
  itemId?: string;
  itemTitle?: string;
  title: string;
  supplierName?: string;
  supplierSku: string;
  supplierCode?: string;
  supplierProductUrl?: string;
  supplierVariant?: string;
  costCents?: number;
  currency?: string;
  notes?: string;
  active?: boolean;
  createdOn?: string;
}

export interface DropshipFulfillmentUpdate {
  fulfillmentStatus?: string;
  supplierPoId?: string;
  trackingNumber?: string;
  trackingCarrier?: string;
  trackingUrl?: string;
  mappingId?: string;
}

export interface SupplierCatalog {
  supplierId: string;
  code: string;
  name: string;
  pushOrder: boolean;
  pullTracking: boolean;
  importCatalog: boolean;
  adapterAvailable: boolean;
  capabilities?: string[];
  docsUrl?: string;
}

export interface SupplierConnection {
  connectionId?: string;
  supplierCode: string;
  supplierName?: string;
  label?: string;
  status: string;
  storeId?: string;
  hasApiKey?: boolean;
  hasApiSecret?: boolean;
  apiKeyMasked?: string;
  apiKeyLength?: number;
  apiSecretLength?: number;
  lastTestedOn?: string;
  lastError?: string;
}

export interface SupplierConnectionRequest {
  supplierCode: string;
  label?: string;
  apiKey?: string;
  apiSecret?: string;
  storeId?: string;
  keepExistingKey?: boolean;
}

export interface SupplierCategory {
  id: string;
  name: string;
  parentId?: string;
  imageUrl?: string;
  sort?: number;
}

export interface SupplierVariant {
  id: string;
  title?: string;
  sku?: string;
  option1?: string;
  option2?: string;
  option3?: string;
  costUsd?: number;
  inventory?: number;
  weightGrams?: number;
  imageUrl?: string;
}

export interface SupplierProduct {
  id: string;
  title: string;
  imageUrl?: string;
  handle?: string;
  status?: number;
  variantCount?: number;
  categoryIds?: string;
  subcategoryIds?: string;
  description?: string;
  imageUrls?: string[];
  variants?: SupplierVariant[];
}

export interface SupplierProductPage {
  supplierCode: string;
  source: string;
  pageIndex: number;
  pageSize: number;
  hasMore: boolean;
  totalHint?: number | null;
  note?: string | null;
  products: SupplierProduct[];
}

export interface DropshipImportListingRequest {
  taxonomySegments: string[];
  title: string;
  description?: string;
  price: number;
  location?: string;
  shipsInDays?: number;
  supplierCode: string;
  supplierSku: string;
  supplierName?: string;
  supplierVariant?: string;
  supplierProductId?: string;
  costCents?: number;
  currency?: string;
  imageUrls?: string[];
  createMapping?: boolean;
  attributes?: Record<string, string | number | boolean>;
}

export interface DropshipImportListingResult {
  itemId: string;
  itemUrl?: string;
  status?: string;
  mappingId?: string;
  message?: string;
}

@Injectable({providedIn: 'root'})
export class DropshipService {
  constructor(private http: HttpClient) {}

  listSuppliers(): Observable<SupplierCatalog[]> {
    return from(environment).pipe(
      switchMap((env: any) =>
        this.http.get<SupplierCatalog[]>(`${env.backendURL}/api/seller/dropship/suppliers`)
      )
    );
  }

  importListing(body: DropshipImportListingRequest): Observable<DropshipImportListingResult> {
    return from(environment).pipe(
      switchMap((env: any) =>
        this.http.post<DropshipImportListingResult>(
          `${env.backendURL}/api/seller/dropship/import-listing`,
          body
        )
      )
    );
  }

  catalogCategories(supplierCode: string, parentId?: string): Observable<SupplierCategory[]> {
    return from(environment).pipe(
      switchMap((env: any) => {
        let params = new HttpParams();
        if (parentId) {
          params = params.set('parentId', parentId);
        }
        return this.http.get<SupplierCategory[]>(
          `${env.backendURL}/api/seller/dropship/catalog/${supplierCode}/categories`,
          {params}
        );
      })
    );
  }

  catalogProducts(
    supplierCode: string,
    opts: {
      categoryId?: string;
      subcategoryId?: string;
      source?: string;
      page?: number;
      size?: number;
    } = {}
  ): Observable<SupplierProductPage> {
    return from(environment).pipe(
      switchMap((env: any) => {
        let params = new HttpParams()
          .set('source', opts.source || 'import')
          .set('page', String(opts.page ?? 0))
          .set('size', String(opts.size ?? 20));
        if (opts.categoryId) {
          params = params.set('categoryId', opts.categoryId);
        }
        if (opts.subcategoryId) {
          params = params.set('subcategoryId', opts.subcategoryId);
        }
        return this.http.get<SupplierProductPage>(
          `${env.backendURL}/api/seller/dropship/catalog/${supplierCode}/products`,
          {params}
        );
      })
    );
  }

  catalogProduct(supplierCode: string, productId: string): Observable<SupplierProduct> {
    return from(environment).pipe(
      switchMap((env: any) =>
        this.http.get<SupplierProduct>(
          `${env.backendURL}/api/seller/dropship/catalog/${supplierCode}/products/${productId}`
        )
      )
    );
  }

  listConnections(): Observable<SupplierConnection[]> {
    return from(environment).pipe(
      switchMap((env: any) =>
        this.http.get<SupplierConnection[]>(`${env.backendURL}/api/seller/dropship/connections`)
      )
    );
  }

  upsertConnection(body: SupplierConnectionRequest): Observable<SupplierConnection> {
    return from(environment).pipe(
      switchMap((env: any) =>
        this.http.put<SupplierConnection>(`${env.backendURL}/api/seller/dropship/connections`, body)
      )
    );
  }

  testConnection(supplierCode: string): Observable<SupplierConnection> {
    return from(environment).pipe(
      switchMap((env: any) =>
        this.http.post<SupplierConnection>(
          `${env.backendURL}/api/seller/dropship/connections/${supplierCode}/test`,
          {}
        )
      )
    );
  }

  disconnect(supplierCode: string): Observable<void> {
    return from(environment).pipe(
      switchMap((env: any) =>
        this.http.delete<void>(`${env.backendURL}/api/seller/dropship/connections/${supplierCode}`)
      )
    );
  }

  fulfillmentQueue(status = 'awaiting_seller'): Observable<OrderView[]> {
    return from(environment).pipe(
      switchMap((env: any) =>
        this.http.get<OrderView[]>(`${env.backendURL}/api/seller/dropship/fulfillment`, {
          params: new HttpParams().set('status', status)
        })
      )
    );
  }

  updateFulfillment(orderId: string, body: DropshipFulfillmentUpdate): Observable<OrderView> {
    return from(environment).pipe(
      switchMap((env: any) =>
        this.http.patch<OrderView>(`${env.backendURL}/api/seller/dropship/fulfillment/${orderId}`, body)
      )
    );
  }

  pushToSupplier(orderId: string): Observable<OrderView> {
    return from(environment).pipe(
      switchMap((env: any) =>
        this.http.post<OrderView>(`${env.backendURL}/api/seller/dropship/fulfillment/${orderId}/push`, {})
      )
    );
  }

  syncTracking(orderId: string): Observable<OrderView> {
    return from(environment).pipe(
      switchMap((env: any) =>
        this.http.post<OrderView>(
          `${env.backendURL}/api/seller/dropship/fulfillment/${orderId}/sync-tracking`,
          {}
        )
      )
    );
  }

  listMappings(activeOnly = true): Observable<DropshipMapping[]> {
    return from(environment).pipe(
      switchMap((env: any) =>
        this.http.get<DropshipMapping[]>(`${env.backendURL}/api/seller/dropship/mappings`, {
          params: new HttpParams().set('activeOnly', String(activeOnly))
        })
      )
    );
  }

  createMapping(body: DropshipMapping): Observable<DropshipMapping> {
    return from(environment).pipe(
      switchMap((env: any) =>
        this.http.post<DropshipMapping>(`${env.backendURL}/api/seller/dropship/mappings`, body)
      )
    );
  }

  updateMapping(mappingId: string, body: Partial<DropshipMapping>): Observable<DropshipMapping> {
    return from(environment).pipe(
      switchMap((env: any) =>
        this.http.put<DropshipMapping>(`${env.backendURL}/api/seller/dropship/mappings/${mappingId}`, body)
      )
    );
  }

  deleteMapping(mappingId: string): Observable<void> {
    return from(environment).pipe(
      switchMap((env: any) =>
        this.http.delete<void>(`${env.backendURL}/api/seller/dropship/mappings/${mappingId}`)
      )
    );
  }
}
